import { useEffect, useState } from "react";
import RightModal from "../components/templates/RightModal";
import { getCustomersList, selectCustomersData, selectCustomersLoading } from "../redux/customers/customersSlice";
import DynamicIcon from "../components/DynamicIcon";
import LoadingSection from "../components/LoadingSection";
import { selectAccountData } from "../redux/main/accountSlice";
import AddEditCustomer from "../components/customersPage/AddEditCustomer";
import ViewCustomer from "../components/customersPage/ViewCustomer";
import { resetRefreshNow, selectRightModalId, selectRightModalIsOpen, selectRightModalIsRefreshNow, selectRightModalmode, setModal } from "../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";
import { useColorPalette } from "../hooks/useColorPalette";
import DataTable from "../components/DataTable";

export default function ProspectsList() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const prospectsList = useSelector(selectCustomersData);
    const prospectsLoading = useSelector(selectCustomersLoading);
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const [searchTerm, setSearchTerm] = useState('');

    // Stati per la modale di eliminazione
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [prospectToDelete, setProspectToDelete] = useState(null);
    const [linkedEntities, setLinkedEntities] = useState({ maintenances: [], jobs: [], assistances: [] });
    const [isCheckingLinks, setIsCheckingLinks] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // RightModal gestita tramite Redux
    const rightModalIsOpen = useSelector(selectRightModalIsOpen);
    const rightModalMode = useSelector(selectRightModalmode);
    const rightModalProspectId = useSelector(selectRightModalId);

    // Funzione per gestire la paginazione
    const handlePageChange = (pageIndex, pageSize) => {
        const start = pageIndex * pageSize;

        dispatch(getCustomersList({
            accountId: accountData?._id,
            token,
            search: searchTerm.length >= 3 ? searchTerm : '',
            start,
            length: pageSize,
            prospect: true
        }));
        dispatch(resetRefreshNow());
    };

    // Funzione per gestire la ricerca
    const handleSearch = (value) => {
        setSearchTerm(value);
        dispatch(getCustomersList({
            accountId: accountData?._id,
            token,
            search: value.length >= 3 ? value : '',
            start: 0,
            length: 25,
            prospect: true
        }));
        dispatch(resetRefreshNow());
    };

    // Chiamata iniziale quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getCustomersList({
                accountId: accountData._id,
                token,
                search: searchTerm.length >= 3 ? searchTerm : '',
                start: 0,
                length: 25,
                prospect: true
            }));
            dispatch(resetRefreshNow());
        }
    }, [dispatch, accountData, searchTerm]);

    // Refresh dei dati quando isRefreshNow della RightModal si attiva
    const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
    useEffect(() => {
        if (rightModalIsRefreshNow) handlePageChange(0, 25);
    }, [rightModalIsRefreshNow]);

    // Verifica collegamenti del prospect prima di eliminarlo
    const handleDeleteClick = async (e, prospect) => {
        e.stopPropagation();
        setProspectToDelete(prospect);
        setIsCheckingLinks(true);
        setShowDeleteModal(true);

        try {
            const prospectId = prospect._id || prospect.id;

            // Verifica manutenzioni collegate
            const maintenancesRes = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/maintenances/lists?accountId=${accountData._id}&customerId=${prospectId}&length=5`,
                { headers: { 'auth-token': token } }
            );
            const maintenancesData = await maintenancesRes.json();

            // Verifica commesse collegate
            const jobsRes = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/jobs/list?accountId=${accountData._id}&customerId=${prospectId}&length=5`,
                { headers: { 'auth-token': token } }
            );
            const jobsData = await jobsRes.json();

            // Verifica richieste di assistenza collegate
            const assistancesRes = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/assistanceRequests/list?accountId=${accountData._id}&customerId=${prospectId}&length=5`,
                { headers: { 'auth-token': token } }
            );
            const assistancesData = await assistancesRes.json();

            setLinkedEntities({
                maintenances: maintenancesData?.data || [],
                jobs: jobsData?.data || [],
                assistances: assistancesData?.data || []
            });
        } catch (error) {
            console.error('Errore nel controllo collegamenti:', error);
            setLinkedEntities({ maintenances: [], jobs: [], assistances: [] });
        } finally {
            setIsCheckingLinks(false);
        }
    };

    // Conferma eliminazione
    const confirmDelete = async () => {
        if (!prospectToDelete) return;

        setIsDeleting(true);
        try {
            const response = await fetch(
                `${import.meta.env.VITE_API_BASE_URL}/customers`,
                {
                    method: 'DELETE',
                    headers: {
                        'auth-token': token,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ customerId: prospectToDelete._id || prospectToDelete.id })
                }
            );

            if (response.ok) {
                handlePageChange(0, 25);
                setShowDeleteModal(false);
                setProspectToDelete(null);
            } else {
                const errorData = await response.json();
                alert(`Errore durante l'eliminazione: ${errorData.message || 'Errore sconosciuto'}`);
            }
        } catch (error) {
            console.error('Errore eliminazione prospect:', error);
            alert('Errore durante l\'eliminazione del prospect');
        } finally {
            setIsDeleting(false);
        }
    };

    // Chiudi modale eliminazione
    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setProspectToDelete(null);
        setLinkedEntities({ maintenances: [], jobs: [], assistances: [] });
    };

    return (
        <div className="page">
            {prospectsLoading && <LoadingSection isFixed />}
            <h1><span className="font-light">Clienti / </span>Potenziali Clienti</h1>
            <div className="box">

                {/* Ricerca e Nuovo prospect */}
                <div className="flex items-center gap-4 mb-4">
                    <div className="relative flex-1">
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <DynamicIcon iconClass="fa-solid fa-search" />
                        </div>
                        <input
                            type="text"
                            className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 pr-10"
                            placeholder="Cerca prospect..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                className="absolute inset-y-0 right-0 flex items-center pe-3"
                                onClick={() => handleSearch('')}
                            >
                                <DynamicIcon iconClass="fa-solid fa-times" />
                            </button>
                        )}
                    </div>
                    <button
                        className="whitespace-nowrap px-4 py-2 flex items-center gap-2"
                        style={{ backgroundColor: palette.primary, color: palette.textOnPrimary, borderRadius: '0.375rem' }}
                        onClick={() => dispatch(setModal({ mode: 'add' }))}
                    >
                        <DynamicIcon iconClass="fa-solid fa-plus" />Nuovo Prospect
                    </button>
                </div>

                {/* Tabella prospect con DataTable */}
                <DataTable
                    columns={[
                        { id: 'type', title: 'TIPO', width: '25%' },
                        { id: 'name', title: 'PROSPECT', width: '25%' },
                        { id: 'fiscal', title: 'P.IVA/C.F.', width: '25%' },
                        { id: 'actions', title: 'AZIONI', width: '25%' }
                    ]}
                    data={prospectsList?.data || []}
                    loading={prospectsLoading}
                    totalRecords={prospectsList?.recordsFiltered || 0}
                    pageSize={25}
                    onPageChange={handlePageChange}
                    onSearch={handleSearch}
                    emptyMessage="Nessun prospect trovato"
                    renderCustomRow={(prospect, index, isEven, tableStyles) => {
                        const prospectId = prospect._id || prospect.id;

                        const handleViewClick = (e) => {
                            e.stopPropagation();
                            dispatch(setModal({
                                mode: 'view',
                                id: prospectId
                            }));
                        };

                        const handleEditClick = (e) => {
                            e.stopPropagation();
                            dispatch(setModal({
                                mode: 'edit',
                                id: prospectId
                            }));
                        };

                        const handleRowClick = () => {
                            dispatch(setModal({
                                mode: 'view',
                                id: prospectId
                            }));
                        };

                        const getTypeIcon = () => {
                            if (prospect.business === true) {
                                return 'fa-industry';
                            }
                            if (prospect.customerType === 'private') {
                                return 'fa-user';
                            } else if (prospect.customerType === 'professional') {
                                return 'fa-briefcase';
                            }
                            return 'fa-building';
                        };

                        return (
                            <div
                                key={prospectId}
                                className="data-table-row cursor-pointer"
                                style={{
                                    backgroundColor: isEven ? tableStyles.evenRow.backgroundColor : tableStyles.oddRow.backgroundColor,
                                    color: isEven ? tableStyles.evenRow.color : tableStyles.oddRow.color,
                                }}
                                onClick={handleRowClick}
                            >
                                {/* Colonna TIPO */}
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="Tipo">
                                    <div className="flex items-center justify-center">
                                        <DynamicIcon iconClass={`fa-solid ${getTypeIcon()}`} size="lg" color="text-slate-400" />
                                    </div>
                                </div>

                                {/* Colonna PROSPECT */}
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="Prospect">
                                    <div className="text-center">
                                        <div className="font-normal uppercase">
                                            {prospect.business
                                                ? (prospect.company || `${prospect.name || ''} ${prospect.surname || ''}`)
                                                : `${prospect.name || ''} ${prospect.surname || ''}`}
                                        </div>
                                        {prospect.email && <div className="text-xs text-gray-500 uppercase">{prospect.email}</div>}
                                    </div>
                                </div>

                                {/* Colonna P.IVA/C.F. */}
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="P.IVA/C.F.">
                                    <div className="font-normal uppercase text-center">
                                        {prospect.business
                                            ? (prospect.vatNumber || '--')
                                            : (prospect.fiscalcode || '--')
                                        }
                                    </div>
                                </div>

                                {/* Colonna AZIONI */}
                                <div className="data-table-cell" style={{ width: '25%' }} data-label="Azioni">
                                    <div className="flex justify-center space-x-2">
                                        <button
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={handleViewClick}
                                            title="Visualizza"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-eye" />
                                        </button>
                                        <button
                                            className="p-1 hover:bg-blue-100 rounded-full"
                                            onClick={handleEditClick}
                                            title="Modifica"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-edit" />
                                        </button>
                                        <button
                                            className="p-1 hover:bg-red-100 rounded-full"
                                            onClick={(e) => handleDeleteClick(e, prospect)}
                                            title="Elimina"
                                        >
                                            <DynamicIcon iconClass="fa-solid fa-trash text-red-600" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                />
            </div>

            {/* RIGHT MODAL per inserimento, modifica o visualizzazione prospect */}
            {
                rightModalIsOpen && <RightModal>
                    {(rightModalMode === 'add' || rightModalMode === 'edit') && <AddEditCustomer customerId={rightModalProspectId} isProspect={true} />}
                    {
                        rightModalMode === 'view' && rightModalProspectId &&
                        <ViewCustomer customerId={rightModalProspectId} />
                    }
                </RightModal>
            }

            {/* MODALE CONFERMA ELIMINAZIONE */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <h3 className="text-xl font-bold mb-4 text-gray-800">
                            Conferma eliminazione prospect
                        </h3>

                        {isCheckingLinks ? (
                            <div className="text-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                                <p className="text-gray-600">Verifica collegamenti in corso...</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-600 mb-4">
                                    Stai per eliminare il prospect: <strong>{prospectToDelete?.label || prospectToDelete?.company || prospectToDelete?.name}</strong>
                                </p>

                                {(linkedEntities.maintenances.length > 0 || linkedEntities.jobs.length > 0 || linkedEntities.assistances.length > 0) && (
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                                        <p className="text-yellow-800 font-medium mb-2">
                                            Attenzione: questo prospect è collegato alle seguenti entità:
                                        </p>

                                        {linkedEntities.maintenances.length > 0 && (
                                            <div className="mb-3">
                                                <p className="text-yellow-700 font-medium text-sm">Manutenzioni ({linkedEntities.maintenances.length}):</p>
                                                <ul className="list-disc list-inside text-sm text-yellow-700">
                                                    {linkedEntities.maintenances.slice(0, 5).map((m, i) => (
                                                        <li key={i}>{m.code || m._id} - {m.systemLabel || 'N/D'}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {linkedEntities.jobs.length > 0 && (
                                            <div className="mb-3">
                                                <p className="text-yellow-700 font-medium text-sm">Commesse ({linkedEntities.jobs.length}):</p>
                                                <ul className="list-disc list-inside text-sm text-yellow-700">
                                                    {linkedEntities.jobs.slice(0, 5).map((j, i) => (
                                                        <li key={i}>{j.code || j._id} - {j.customerLabel || j.systemLabel || j.description || 'N/D'}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {linkedEntities.assistances.length > 0 && (
                                            <div>
                                                <p className="text-yellow-700 font-medium text-sm">Richieste Assistenza ({linkedEntities.assistances.length}):</p>
                                                <ul className="list-disc list-inside text-sm text-yellow-700">
                                                    {linkedEntities.assistances.slice(0, 5).map((a, i) => (
                                                        <li key={i}>{a.code || a._id} - {a.customerLabel || a.description || 'N/D'}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        <p className="text-yellow-800 text-sm mt-3">
                                            Eliminando il prospect, queste entità perderanno il collegamento logico.
                                        </p>
                                    </div>
                                )}

                                <p className="text-gray-600 mb-6">
                                    Sei sicuro di voler procedere con l&apos;eliminazione?
                                </p>

                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={closeDeleteModal}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                        disabled={isDeleting}
                                    >
                                        Annulla
                                    </button>
                                    <button
                                        onClick={confirmDelete}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? 'Eliminazione...' : 'Elimina'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
